// This pragma is required for the css utility to work
/** @jsx jsx */
import { css, SerializedStyles } from '@emotion/react';
import styled from '@emotion/styled';
import { Property } from 'csstype';
import * as React from 'react';
import { colors, fontWeight } from '~/styles/constants';
import { CSSLengthUnit } from '~/utils/css';

export type VerticalAlignment =
  | 'baseline'
  | 'sub'
  | 'super'
  | 'top'
  | 'text-top'
  | 'middle'
  | 'bottom'
  | 'text-bottom'
  | 'initial'
  | 'inherit'
  | CSSLengthUnit;

interface CellCommonProps {
  rowIndex?: number;
  isHeader?: boolean;
  width?: string;
  minWidth?: string;
  colSpan?: number;
  noLeftPadding?: boolean;
  noRightPadding?: boolean;
  noPadding?: boolean;
  verticalAlign?: VerticalAlignment;
  cursor?: Property.Cursor;
  customStyles?: SerializedStyles;
  asGrid?: boolean;
}

interface InnerCellProps extends CellCommonProps {
  onClick?: (evt: React.MouseEvent<HTMLTableCellElement, MouseEvent>) => void;
}
export interface CellProps extends CellCommonProps {
  onClick?: (rowIndex: number) => void;
  children?: React.ReactNode;
}

const cellStyles = css`
  display: table-cell;
  height: inherit;
  padding: 15px 15px;
  vertical-align: middle;
  font-size: 13px;
  line-height: 18px;
  font-weight: ${fontWeight.normal};

  :first-of-type {
    padding-left: 10px;
  }

  :last-of-type {
    padding-right: 10px;
  }
`;

const Cell = styled('td')<InnerCellProps>`
  ${cellStyles};
  ${(props) =>
    props.cursor ? `cursor: ${props.cursor};` : props.onClick ? 'cursor: pointer;' : ''}

  color: ${colors.gray700};

  ${(props) => (props.noLeftPadding ? 'padding-left: 0;' : '')}
  ${(props) => (props.noRightPadding ? 'padding-right: 0;' : '')}
  ${({ noPadding }) => (noPadding ? 'padding: 0;' : '')}
  ${(props) => (props.verticalAlign ? `vertical-align: ${props.verticalAlign};` : '')}
  ${(props) => props.customStyles};
`;

const Header = styled('th')<InnerCellProps>`
  ${cellStyles};
  color: ${colors.secondary};
  font-size: 12px;
  font-weight: 400;
  width: ${(props) => props.width || 'inherit'};
  ${(props) => (props.minWidth ? `min-width: ${props.minWidth};` : '')}
  text-align: left;

  ${(props) => (props.noLeftPadding ? 'padding-left: 0;' : '')}
  ${(props) => (props.noRightPadding ? 'padding-right: 0;' : '')}
  ${(props) => props.customStyles}
`;

const TableCell = (props: CellProps) => {
  const {
    children,
    cursor,
    customStyles,
    colSpan,
    noLeftPadding,
    noRightPadding,
    noPadding,
    onClick,
    rowIndex,
    isHeader,
    verticalAlign,
    width,
    asGrid,
  } = props;

  const onClickCallback = React.useCallback(
    (evt) => {
      if (onClick && rowIndex !== undefined) {
        evt.stopPropagation();
        return onClick(rowIndex);
      }
    },
    [onClick, rowIndex],
  );

  const CellComponent = isHeader ? Header : Cell;

  return (
    <CellComponent
      colSpan={colSpan}
      isHeader={isHeader}
      width={width}
      noLeftPadding={noLeftPadding}
      noRightPadding={noRightPadding}
      noPadding={noPadding}
      verticalAlign={verticalAlign}
      onClick={onClick ? onClickCallback : undefined}
      cursor={cursor}
      customStyles={customStyles}
      asGrid={asGrid}
    >
      {children}
    </CellComponent>
  );
};

const ComposedTableCell = React.memo(TableCell);

export default ComposedTableCell as React.ComponentType<CellProps>;
